// Simple Design Tool Component
import React, { useState, useRef, useEffect } from 'react';

const SimpleDesignTool = ({ onSaveDesign, onClose }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(3);
  const [text, setText] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = 600;
    canvas.height = 400;
    
    // Set white background
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const startDrawing = (e) => {
    if (tool === 'text') {
      const rect = canvasRef.current.getBoundingClientRect();
      setTextPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setShowTextInput(true);
      return;
    }

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    setIsDrawing(true);
    context.beginPath();
    context.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    context.strokeStyle = color;
    context.lineWidth = brushSize;
    context.lineCap = 'round';
  };

  const draw = (e) => {
    if (!isDrawing || tool === 'text') return;
    
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    context.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    context.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const addText = () => {
    if (!text.trim()) return;
    
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    context.font = '16px Arial';
    context.fillStyle = color;
    context.fillText(text, textPosition.x, textPosition.y);
    
    setText('');
    setShowTextInput(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveDesign = () => {
    const canvas = canvasRef.current;
    const imageData = canvas.toDataURL('image/png');
    onSaveDesign(imageData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Simple Design Tool</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-gray-100 rounded">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Tool:</label>
            <button
              onClick={() => setTool('pen')}
              className={`px-3 py-1 rounded ${tool === 'pen' ? 'bg-blue-500 text-white' : 'bg-white'}`}
            >
              ✏️ Draw
            </button>
            <button
              onClick={() => setTool('text')}
              className={`px-3 py-1 rounded ${tool === 'text' ? 'bg-blue-500 text-white' : 'bg-white'}`}
            >
              📝 Text
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Color:</label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-8 h-8 rounded border"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Size:</label>
            <input
              type="range"
              min="1"
              max="20"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              className="w-16"
            />
            <span className="text-sm w-6">{brushSize}</span>
          </div>
          
          <button
            onClick={clearCanvas}
            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Clear
          </button>
        </div>
        
        {/* Canvas */}
        <div className="relative border border-gray-300 rounded mb-4">
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            className="cursor-crosshair"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
          
          {/* Text input overlay */}
          {showTextInput && (
            <div
              className="absolute bg-white border rounded p-2 shadow-lg"
              style={{
                left: textPosition.x,
                top: textPosition.y,
                transform: 'translate(-50%, -100%)'
              }}
            >
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter text..."
                className="border rounded px-2 py-1 text-sm"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') addText();
                }}
              />
              <div className="mt-1 flex gap-2">
                <button
                  onClick={addText}
                  className="px-2 py-1 bg-blue-500 text-white text-xs rounded"
                >
                  Add
                </button>
                <button
                  onClick={() => setShowTextInput(false)}
                  className="px-2 py-1 bg-gray-500 text-white text-xs rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Instructions */}
        <div className="text-sm text-gray-600 mb-4 p-3 bg-blue-50 rounded">
          <p className="font-medium mb-1">How to use:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Draw:</strong> Select pen tool and draw on the canvas</li>
            <li><strong>Add Text:</strong> Select text tool and click where you want to add text</li>
            <li><strong>Change Colors:</strong> Use the color picker to change pen/text color</li>
            <li><strong>Adjust Size:</strong> Use the slider to change pen thickness</li>
          </ul>
        </div>
        
        {/* Action buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={saveDesign}
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
          >
            Save Design
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleDesignTool;